package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.width
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LFilterBar
import dev.lingvo.site.ds.LFilterField
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.px

/**
 * How many of how many, said under the controls that decided it.
 *
 * "48 of 1,204" rather than "48 results": a filtered list whose total is invisible cannot tell the
 * reader whether their search was narrow or their project is small.
 */
internal fun keysResultSummary(shown: Int, total: Int): String = if (shown >= total) {
    trf(
        "app.projects.slug.keys_filter_bar.result_summary_all",
        "{total} keys",
        "total" to formatCount(total),
    )
} else {
    trf(
        "app.projects.slug.keys_filter_bar.result_summary",
        "{shown} of {total}",
        "shown" to formatCount(shown),
        "total" to formatCount(total),
    )
}

@Composable
internal fun KeysFilterBar(
    slug: String,
    search: String,
    onSearchChange: (String) -> Unit,
    files: List<FileDto>,
    fileId: String,
    onFileIdChange: (String) -> Unit,
    targetLocales: List<String>,
    locale: String,
    onLocaleChange: (String) -> Unit,
    stateFilter: KeyStateFilter,
    onStateFilterChange: (KeyStateFilter) -> Unit,
    tag: String,
    onTagChange: (String) -> Unit,
    shown: Int,
    total: Int,
    onClearAll: () -> Unit,
    /**
     * The run the current filter makes obvious, or null where there is none.
     *
     * Present only for an admin looking at an untranslated set in a named language: the count is
     * real, the language is on screen, and the server would refuse anybody else.
     */
    onPretranslateFiltered: (() -> Unit)? = null,
) {
    val fields = buildList {
        add(
            LFilterField(
                id = "project-keys-filter-search-$slug",
                label = t("app.projects.slug.keys_filter_bar.search", "Search"),
                activeValue = search.takeIf { it.isNotBlank() },
                onClear = { onSearchChange("") },
            ) { modifier ->
                LSearchField(
                    value = search,
                    onValueChange = onSearchChange,
                    modifier = modifier
                        .flexGrow(1)
                        .maxWidth(520.px)
                        .attr("id", "project-keys-search-$slug"),
                    label = t("app.projects.slug.keys_filter_bar.search_keys", "Search keys"),
                    placeholder = t("app.projects.slug.keys_filter_bar.search_names_source_strings_or_context", "Search names, source strings, or context"),
                    shortcutHint = null,
                )
            },
        )
        add(
            LFilterField(
                id = "project-keys-filter-file-$slug",
                label = t("app.projects.slug.keys_filter_bar.file", "File"),
                activeValue = files.firstOrNull { it.id == fileId }?.path,
                onClear = { onFileIdChange("") },
            ) { modifier ->
                LSelect(
                    value = fileId,
                    options = listOf(LSelectOption("", t("app.projects.slug.keys_filter_bar.all_files", "All files"))) +
                        files.map { LSelectOption(it.id, it.path) },
                    onValueChange = onFileIdChange,
                    modifier = modifier.width(240.px),
                    label = t("app.projects.slug.keys_filter_bar.file", "File"),
                )
            },
        )
        // Paired rather than shown alone: the server reads "state" against one target locale's
        // translation (`KeyRepo.list` joins `translations` on exactly the locale it is given), so a
        // state filter with nothing to name a locale would silently mean nothing. A project with no
        // target locale at all has no translation state to filter by, so neither control appears.
        if (targetLocales.isNotEmpty()) {
            add(
                LFilterField(
                    id = "project-keys-filter-locale-$slug",
                    label = t("app.projects.slug.keys_filter_bar.locale", "Language"),
                    // Never cleared to nothing: "state in no language" is not a question the server
                    // can answer, so the language picker has no empty position to clear back to.
                    activeValue = null,
                ) { modifier ->
                    LSelect(
                        value = locale,
                        options = targetLocales.map { code ->
                            LSelectOption(code, "${localeFlagPrefix(code)}${localeInfo(code).englishName}")  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
                        },
                        onValueChange = onLocaleChange,
                        modifier = modifier.width(200.px),
                        label = t("app.projects.slug.keys_filter_bar.locale", "Language"),
                    )
                },
            )
            add(
                LFilterField(
                    id = "project-keys-filter-state-$slug",
                    label = t("app.projects.slug.keys_filter_bar.state", "State"),
                    activeValue = stateFilter.takeIf { it != KeyStateFilter.ANY }?.label(),
                    onClear = { onStateFilterChange(KeyStateFilter.ANY) },
                ) { modifier ->
                    LSelect(
                        value = stateFilter.name,
                        options = KeyStateFilter.entries.map { LSelectOption(it.name, it.label()) },
                        onValueChange = { raw ->
                            KeyStateFilter.entries.firstOrNull { it.name == raw }?.let(onStateFilterChange)
                        },
                        modifier = modifier.width(180.px),
                        // The picker beside this one resolves the same code to "🇩🇪 German"; this said
                        // "State in de" and left the reader to remember that the two controls name the
                        // same language. A raw locale code is also the one string in this bar a
                        // translator cannot read in their own.
                        label = trf(
                            "app.projects.slug.keys_filter_bar.state_in",
                            "State in {language}",
                            "language" to localeInfo(locale).englishName,
                        ),
                    )
                },
            )
        }
        add(
            LFilterField(
                id = "project-keys-filter-tag-$slug",
                label = t("app.projects.slug.keys_filter_bar.tag", "Tag"),
                activeValue = tag.takeIf { it.isNotBlank() },
                onClear = { onTagChange("") },
            ) { modifier ->
                LTextField(
                    value = tag,
                    onValueChange = onTagChange,
                    modifier = modifier.width(200.px),
                    label = t("app.projects.slug.keys_filter_bar.tag", "Tag"),
                    placeholder = "checkout",
                )
            },
        )
    }

    LFilterBar(
        fields = fields,
        onClearAll = onClearAll,
        label = t("app.projects.slug.keys_filter_bar.key_filters", "Key filters"),
        resultSummary = keysResultSummary(shown, total),
        actions = onPretranslateFiltered?.let { start ->
            {
                LButton(
                    label = tf(
                        "app.projects.slug.keys_filter_bar.pretranslate_untranslated_in_language",
                        "Pre-translate {count} untranslated in {language}",
                        "count" to formatCount(total),
                        "language" to localeInfo(locale).englishName,
                    ),
                    onClick = start,
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LWandSparklesIcon() },
                )
            }
        },
    )
}
